import { type ReactNode } from "react";

const SAFE_URL_SCHEMES = /^https?:\/\//i;

// Bare http(s) URL up to the next whitespace or `<` (so it never swallows a
// following tag). Mirrors the autolinker MessageContent uses on plain content.
const BARE_URL_REGEX = /https?:\/\/[^\s<]+/g;

/**
 * AS-1927: autolink bare URLs inside a plain-text segment (a chunk that is NOT
 * already an `<a>` anchor). The AI-screener introduction always begins with the
 * disclaimer's `<a>` Terms/Privacy links, which routes the whole message
 * through this HTML path — so without this, a bare URL typed into the opening
 * statement renders as inert text. Conclusion/basic-question links worked only
 * because those messages contain no `<a>` and went through MessageContent's
 * bare-URL autolinker instead.
 */
function linkifyBareUrls(
  text: string,
  segmentKey: number,
  linkClassName?: string,
): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(BARE_URL_REGEX);

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Trailing prose punctuation ("…at https://example.com.") is not part of
    // the URL — keep it out of the href but visible as plain text.
    const rawUrl = match[0];
    let url = rawUrl.replace(/[),.!?;:]+$/u, "");
    let trailingText = rawUrl.slice(url.length);

    // Keep one trailing ')' in the URL when it closes an unmatched '(' in-path
    // (e.g. Wikipedia-style "...wiki/Foo_(bar)").
    if (trailingText.startsWith(")")) {
      const openingParens = (url.match(/\(/g) ?? []).length;
      const closingParens = (url.match(/\)/g) ?? []).length;
      if (openingParens > closingParens) {
        url += ")";
        trailingText = trailingText.slice(1);
      }
    }
    parts.push(
      <a
        key={`seg_${segmentKey}_url_${parts.length}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {url}
      </a>,
    );
    if (trailingText.length > 0) {
      parts.push(trailingText);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

export function parseLinkedString(
  message: string,
  linkClassName?: string,
): ReactNode[] {
  return message.split(/(<a.*?<\/a>)/).flatMap((part, index) => {
    if (part.startsWith("<a") && part.endsWith("</a>")) {
      const hrefMatch = part.match(/href="(.*?)"/);
      const rawHref = hrefMatch?.[1] ?? "#";
      const href = SAFE_URL_SCHEMES.test(rawHref) ? rawHref : "#";
      const text = part.replace(/<\/?a.*?>/g, "");

      return [
        <a key={index} href={href} target="_blank" rel="noopener noreferrer">
          {text}
        </a>,
      ];
    }

    // Non-anchor segment: autolink any bare URLs it contains (AS-1927).
    return linkifyBareUrls(part, index, linkClassName);
  });
}
